import React from "react"
import { useQuery } from "@apollo/client"
import { useHistory } from "react-router-dom"
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil"
import { connect, TCExtensionAPI } from "trimble-connect-project-workspace-api"
import locationToOrigin from "../util/locationToOrigin"
import {
  extensionApiState,
  projectIdState,
  projectOriginState,
  quadriProjectIdState,
  needsLicenseState,
  hasProjectAccessState,
  notificationsState,
  featureCatalogIdState,
  signedInUserState,
  userClaimsState
} from "./StateSelectors"
import { PendingSpinner } from "./Pending"
import { GET_PROJECT_STATUS, HAS_ACCESS, GET_NOTIFICATIONS, HAS_PROJECT_ACCESS } from "../api/QuadriApi"
import Oops from "./Oops/Oops"
import { getMenu } from "../util/getMenu"
import { useTranslation } from "react-i18next"
import { exceptionFromApolloError } from "../util/apolloError"
import { SetEnvironmentDefault, SetEnvironmentProdEU, SetEnvironmentProdUS } from "../enviroments"
import { ApolloProvider } from "@apollo/client"
import { QuadriApi } from "../api/QuadriApi"
import { identify } from "../util/gainsight"

interface ConnectExtensionProps {
  children: React.ReactElement
}

const ConnectExtension = (props: ConnectExtensionProps) => {
  const [extensionAuthorized, setExtensionAuthorized] = React.useState(true)
  const [extensionApi, setExtensionApi] = useRecoilState(extensionApiState)
  const [projectId, setProjectId] = useRecoilState(projectIdState)
  const [, setUserClaims] = useRecoilState(userClaimsState)
  const setProjectOrigin = useSetRecoilState(projectOriginState)
  const [, setSignedInUser] = useRecoilState(signedInUserState)
  const { i18n } = useTranslation()
  const { t } = useTranslation("common")
  // Flag to delay rendering of the rest of the app until the correct region has been set
  const [connectProjectFetched, setConnectProjectFetched] = React.useState(false)

  const [reloading, setReloading] = React.useState(false)

  const isIframe = window !== window.parent

  const history = useHistory()

  const handleCommand = async (command: string) => {
    // remove the query string from the URL
    const baseCommand = command.split("?")[0]
    const query = command.split("?")[1]

    switch (baseCommand) {
      case "config":
        history.replace("/config")
        // Workround for missing notifications from Connect
        window.location.reload()
        setReloading(true)
        break
      case "top_menu":
        // history.replace("/config") // Should the top menu do anything?
        break
      case "tasks":
        history.replace("/tasks")
        break
      case "timeline":
        history.replace("/timeline")
        break
      case "worksets":
        history.replace("/worksets" + (query ? "?" + query : ""))
        break
      default:
        console.log("Got unknown command: ", command)
    }
  }

  const setAccessToken = (accessToken?: string) => {
    if (accessToken) {
      if (accessToken === "Denied") {
        setExtensionAuthorized(false)
        return
      }
      const payload = accessToken.split(".")[1]
      if (payload) {
        localStorage.setItem("tc-access-token", accessToken)
        const claims = JSON.parse(window.atob(payload))
        console.log("Claims: ", claims)
        identify(claims.sub)
        setUserClaims(claims)
      }
    }
  }

  React.useEffect(() => {
    const asyncStuff = async () => {
      const extApi = await connect(
        window.parent,
        (event, args) => {
          console.log("Extensions API event: ", event, " With args: ", args)
          switch (event) {
            case "extension.command":
              handleCommand(args.data)
              break
            case "extension.accessToken":
              console.log("Got access token refresh event!")
              setAccessToken(args.data)
              break
            case "extension.userSettingsChanged":
              //"User settings changed!"
              break
          }
        },
        30000
      )
      setExtensionApi(extApi)
      // TODO: Is this an error in the Typescript files, or in the documentation. See
      // https://components.connect.trimble.com/trimble-connect-project-workspace-api/docs/index.html
      // Confirmed that this is an error in the documentation
      const accessTokenPromise = extApi.extension.getPermission("accesstoken") as unknown as Promise<string>
      const projectPromise = extApi.project.getCurrentProject()
      const languagePromise = extApi.user.getUserSettings().then((s: any) => {
        console.log("user settings: ", s)
        setSignedInUser(s.email)
        return i18n.changeLanguage(getLangCodeIso639_1(s.language))
      })

      const [accessToken, project] = await Promise.all([accessTokenPromise, projectPromise, languagePromise])

      setAccessToken(accessToken)
      setProjectId(project.id)
      console.log("project: ", project)

      if (project.location) {
        console.log("Project location: ", project.location)
        if (project.location === "europe") {
          SetEnvironmentProdEU()
        } else if (project.location === "northAmerica") {
          SetEnvironmentProdUS()
        } else {
          console.error("Unknown project location: ", project.location)
          SetEnvironmentDefault()
        }
        setProjectOrigin(locationToOrigin(project.location))
      } else {
        console.error("Project location not set!")
        SetEnvironmentDefault()
      }

      // Make sure that the correct environment has been set
      setConnectProjectFetched(true)
    }

    asyncStuff()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!extensionAuthorized) {
    extensionApi?.extension.setStatusMessage(t("notAuthorized.heading"))
    return <Oops heading={t("notAuthorized.heading")} body={t("notAuthorized.body")} tryAgainDisabled={true} />
  }

  // Check if we have a token yet
  const token = localStorage.getItem("tc-access-token")

  return token && extensionApi && projectId && !reloading && connectProjectFetched ? ( // For running in Connect
    <ApolloProvider client={new QuadriApi().getClient()}>
      <ConnectExtensionInner extensionApi={extensionApi} content={props.children} />
    </ApolloProvider>
  ) : (
    <PendingSpinner />
  )
}

interface ConnectExtensionInnerProps {
  extensionApi: TCExtensionAPI
  content: React.ReactElement
}

const ConnectExtensionInner = (props: ConnectExtensionInnerProps) => {
  const projectId = useRecoilValue(projectIdState)
  const { t } = useTranslation("common")

  if (!projectId) {
    throw new Error("Connect project ID need to be set before loading project status")
  }

  const [, setQuadriProjectId] = useRecoilState(quadriProjectIdState)
  const [, setNeedsLicense] = useRecoilState(needsLicenseState)
  const [hasProjectAccess, setHasProjectAccess] = useRecoilState(hasProjectAccessState)
  const [, setNotifications] = useRecoilState(notificationsState)
  const [, setFeatureCatalogId] = useRecoilState(featureCatalogIdState)
  const [signedInUser] = useRecoilState(signedInUserState)
  const [loaded, setLoaded] = React.useState(false)

  const history = useHistory()

  React.useEffect(() => {
    history.listen((location: any, action: any) => {
      // The below way of setting the active menu item does not work, because not only
      // does it set the active menu item, but it also navigates to that submenu. We need
      // a way to just update the UI without affecting internal page navigation
      // // command, ie "tasks"
      // const command = location.pathname.split("/")[1]
      // props.extensionApi.ui.setActiveMenuItem(command)
    })
  }, [history])

  const projectAccessQuery = useQuery(HAS_PROJECT_ACCESS, {
    variables: { connectProjectId: projectId }
  })

  React.useEffect(() => {
    const hasAccess = projectAccessQuery.data?.hasProjectAccess.projectAccess
    if (hasAccess !== undefined) {
      setHasProjectAccess(hasAccess)
    }
  }, [projectAccessQuery.data, setHasProjectAccess])

  const accessQuery = useQuery(HAS_ACCESS)
  const notificationsQuery = useQuery(GET_NOTIFICATIONS)

  React.useEffect(() => {
    const notifications = notificationsQuery.data?.getNotifications
    setNotifications(notifications)
  }, [notificationsQuery.data, setNotifications])

  React.useEffect(() => {
    const needsLicense = !accessQuery.data?.hasAccess?.access
    setNeedsLicense(needsLicense)
  }, [accessQuery.data, setNeedsLicense])

  const {
    error,
    data,
    loading: projectLoading
  } = useQuery(GET_PROJECT_STATUS, {
    skip: !hasProjectAccess,
    variables: { id: projectId },
    onCompleted: (data) => {
      // This query sets the `quadri project ID` used in subsequent queries to GraphQL
      const response = data.tryGetConnectQuadriProject
      if (response.found) {
        setQuadriProjectId(response.project.quadriProjectId)
        console.log("Set Quadri Project Id: ", response.project.quadriProjectId)
        setFeatureCatalogId(response.project.featureCatalogId)
        console.log("Set Feature Catalogue Id: ", response.project.featureCatalogId)

        const status = response.project.projectStatus
        if (status === "Active" || status === "Updating") {
          props.extensionApi.ui.setMenu(getMenu(t))
        }
      }
      setLoaded(true) // This can only be set after quadri project id.
    }
  })

  if (hasProjectAccess === undefined) {
    props.extensionApi.extension.setStatusMessage(t("fetchingStatus"))
    return <PendingSpinner />
  }

  if (!hasProjectAccess) {
    props.extensionApi.extension.setStatusMessage(t("error.heading.NO_USER_ACCESS"))
    const noAccessMsg = t("error.NO_USER_ACCESS").replace("{{email}}", signedInUser!)
    return <Oops heading={t("error.heading.NO_USER_ACCESS")} body={noAccessMsg} isAuthError={true} />
  }

  const getProjectError = data?.tryGetConnectQuadriProject?.error

  if (error) {
    const errorMessage = exceptionFromApolloError(error).message
    console.error(errorMessage)
    props.extensionApi.extension.setStatusMessage(t("failedToFetchStatus"))
    return <Oops body={errorMessage} />
  } else if (getProjectError) {
    props.extensionApi.extension.setStatusMessage(t("failedToFetchStatus"))
    return <Oops body={getProjectError} />
  } else if (accessQuery.error) {
    const errorMessage = exceptionFromApolloError(accessQuery.error).message
    console.error(errorMessage)
    return <Oops body={errorMessage} />
  } else if (notificationsQuery?.error) {
    const errorMessage = exceptionFromApolloError(notificationsQuery.error).message
    console.error(errorMessage)
    return <Oops body={errorMessage} />
  } else if (!loaded || accessQuery.loading || notificationsQuery?.loading || projectLoading) {
    props.extensionApi.extension.setStatusMessage(t("fetchingStatus"))
    return <PendingSpinner />
  } else {
    const isFound = data?.tryGetConnectQuadriProject?.found
    const projectStatus = data?.tryGetConnectQuadriProject?.project?.projectStatus

    if (isFound && projectStatus) {
      props.extensionApi.extension.setStatusMessage(t("projectStatus." + projectStatus))
    } else {
      props.extensionApi.extension.setStatusMessage(t("readyToEnable"))
    }
    return props.content
  }
}

const getLangCodeIso639_1 = (language: string) => language.split("-")[0]

export default ConnectExtension
